import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  CheckCircle,
  Clock3,
  GitCompareArrows,
  History,
  Info,
  Landmark,
  Loader2,
  Wallet,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { adminBankTransferConfigService } from "@/services/admin-bank-transfer-config.service";
import { bankTransferSettingsSchema } from "@/schemas/bank-transfer-settings";
import type { BankTransferSettingsFormData } from "@/schemas/bank-transfer-settings";
import type { BankTransferConfig } from "@/types/bank-transfer-config";
import { formatDateTime } from "@/lib/utils";
import {
  SettingsField,
  SettingsFormFooter,
  SettingsFormSkeleton,
  SettingsLoadError,
  SettingsSection,
} from "./SettingsForm";

const FIELD_LABELS: Record<string, string> = {
  bankName: "Bank Name",
  accountNumber: "Account Number",
  accountName: "Account Holder",
  branch: "Branch",
  swiftCode: "SWIFT Code",
};

function configToForm(
  config: BankTransferConfig,
): BankTransferSettingsFormData {
  return {
    bankName: config.bankName ?? "",
    accountNumber: config.accountNumber ?? "",
    accountName: config.accountName ?? "",
    branch: config.branch ?? "",
    swiftCode: config.swiftCode ?? "",
  };
}

export function BankTransferSettingsTab() {
  const queryClient = useQueryClient();
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const {
    data: config,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["admin", "platform-settings", "bank-transfer-config"],
    queryFn: () => adminBankTransferConfigService.getBankTransferConfig(),
  });

  const form = useForm<BankTransferSettingsFormData>({
    resolver: zodResolver(bankTransferSettingsSchema),
    defaultValues: {
      bankName: "",
      accountNumber: "",
      accountName: "",
      branch: "",
      swiftCode: "",
    },
  });

  useEffect(() => {
    if (config?.current) {
      form.reset(configToForm(config.current));
    }
  }, [config, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: BankTransferSettingsFormData) => {
      return await adminBankTransferConfigService.updateBankTransferConfig(
        data,
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings", "bank-transfer-config"],
      });
      toast.success("Bank transfer details saved");
      form.reset(configToForm(data.current));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => adminBankTransferConfigService.syncBankTransferConfig(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings", "bank-transfer-config"],
      });
      toast.success("Deployed bank details applied successfully");
      setSyncDialogOpen(false);
      form.reset(configToForm(data.current));
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => adminBankTransferConfigService.rejectBankTransferConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings", "bank-transfer-config"],
      });
      toast.success("Deployment change dismissed. Current values kept.");
      setRejectDialogOpen(false);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleError = (errors: Record<string, unknown>) => {
    const firstError = Object.values(errors)[0];
    if (
      firstError &&
      typeof firstError === "object" &&
      "message" in firstError
    ) {
      toast.error(String(firstError.message));
    }
  };

  const { errors, isDirty } = form.formState;
  const isDrift = config?.isDrift ?? false;
  const driftedFields = config?.driftedFields ?? [];

  return (
    <div className="space-y-5">
      {config && isDrift && (
        <DriftAlert
          current={config.current}
          deployed={config.deployed}
          driftedFields={driftedFields}
          onAccept={() => setSyncDialogOpen(true)}
          onReject={() => setRejectDialogOpen(true)}
          isAccepting={syncMutation.isPending}
          isRejecting={rejectMutation.isPending}
        />
      )}

      <div className="grid items-start gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="overflow-hidden shadow-sm">
          <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 border-b bg-muted/30 p-5">
            <div className="space-y-1.5">
              <CardTitle className="flex items-center gap-2">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Landmark className="size-4" />
                </span>
                Bank Transfer Configuration
              </CardTitle>
              <CardDescription className="max-w-2xl leading-6">
                The account customers send bank transfers to.
              </CardDescription>
            </div>
            {config && <SyncBadge isDrift={isDrift} />}
          </CardHeader>

          {isLoading ? (
            <SettingsFormSkeleton rows={5} />
          ) : !config ? (
            <SettingsLoadError onRetry={() => void refetch()} />
          ) : (
            <form
              onSubmit={form.handleSubmit(
                (data) => updateMutation.mutate(data),
                handleError,
              )}
            >
              <CardContent className="divide-y p-0">
                <SettingsSection
                  title="Account"
                  description="Shown to customers who choose bank transfer."
                >
                  <SettingsField
                    id="bankName"
                    label="Bank name"
                    error={errors.bankName?.message}
                  >
                    <Input
                      id="bankName"
                      placeholder="e.g., Vietcombank"
                      {...form.register("bankName")}
                    />
                  </SettingsField>
                  <div className="grid gap-4 md:grid-cols-2">
                    <SettingsField
                      id="accountNumber"
                      label="Account number"
                      error={errors.accountNumber?.message}
                    >
                      <Input
                        id="accountNumber"
                        inputMode="numeric"
                        placeholder="e.g., 1234567890"
                        autoComplete="off"
                        className="font-mono"
                        {...form.register("accountNumber")}
                      />
                    </SettingsField>
                    <SettingsField
                      id="accountName"
                      label="Account holder"
                      error={errors.accountName?.message}
                    >
                      <Input
                        id="accountName"
                        placeholder="e.g., COMPANY NAME"
                        autoComplete="off"
                        {...form.register("accountName")}
                      />
                    </SettingsField>
                  </div>
                </SettingsSection>

                <SettingsSection
                  title="Branch details"
                  description="Optional. Shown only when filled in."
                >
                  <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_200px]">
                    <SettingsField
                      id="branch"
                      label="Branch"
                      error={errors.branch?.message}
                    >
                      <Input
                        id="branch"
                        placeholder="e.g., Ho Chi Minh City Branch"
                        {...form.register("branch")}
                      />
                    </SettingsField>
                    <SettingsField
                      id="swiftCode"
                      label="SWIFT code"
                      error={errors.swiftCode?.message}
                    >
                      <Input
                        id="swiftCode"
                        placeholder="e.g., BFTVVNBX"
                        autoComplete="off"
                        spellCheck={false}
                        className="font-mono"
                        {...form.register("swiftCode")}
                      />
                    </SettingsField>
                  </div>
                </SettingsSection>
              </CardContent>

              <SettingsFormFooter
                isDirty={isDirty}
                isSaving={updateMutation.isPending}
                onDiscard={() => form.reset(configToForm(config.current))}
              />
            </form>
          )}
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="p-5 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Info className="size-4 text-primary" /> How it goes live
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-0">
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2.5">
                <Wallet className="mt-0.5 size-4 shrink-0" />
                <span>
                  Customers see these details on the Deposit page when they
                  choose bank transfer.
                </span>
              </li>
              {/* BankTransferSettingsService caches the details for 60 seconds per API pod. */}
              <li className="flex gap-2.5">
                <Clock3 className="mt-0.5 size-4 shrink-0" />
                <span>
                  Saved changes reach customers within about a minute.
                </span>
              </li>
              {/* "Deployed" is PaymentOptions.BankTransfer, set through the Helm values. */}
              <li className="flex gap-2.5">
                <GitCompareArrows className="mt-0.5 size-4 shrink-0" />
                <span>
                  A deployment can carry its own bank details. When they differ,
                  a banner asks you to accept them or keep the current values —
                  nothing changes until you choose.
                </span>
              </li>
              {config && (
                <li className="flex gap-2.5">
                  <History className="mt-0.5 size-4 shrink-0" />
                  <p>
                    Last updated:{" "}
                    {config.lastUpdatedAt
                      ? formatDateTime(config.lastUpdatedAt)
                      : "never"}
                  </p>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept deployed bank details?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              This will replace the current bank transfer details with the
              values from the deployed configuration. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                syncMutation.mutate();
              }}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                "Apply deployed values"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject deployed configuration?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              This will keep the current bank transfer details and dismiss the
              deployment change. The drift warning will be removed.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                rejectMutation.mutate();
              }}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Dismissing...
                </>
              ) : (
                "Keep current values"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SyncBadge({ isDrift }: { isDrift: boolean }) {
  return isDrift ? (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-warn-soft px-2.5 py-1 text-xs font-medium text-warn-ink">
      <span className="size-1.5 animate-pulse rounded-full bg-warn" />
      Deployment drift
    </span>
  ) : (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ok-soft px-2.5 py-1 text-xs font-medium text-ok-ink">
      <span className="size-1.5 rounded-full bg-ok" />
      In sync
    </span>
  );
}

function DriftAlert({
  current,
  deployed,
  driftedFields,
  onAccept,
  onReject,
  isAccepting,
  isRejecting,
}: {
  current: BankTransferConfig;
  deployed: BankTransferConfig;
  driftedFields: string[];
  onAccept: () => void;
  onReject: () => void;
  isAccepting: boolean;
  isRejecting: boolean;
}) {
  return (
    <section
      className="rounded-lg border border-warn/25 bg-warn-soft p-4"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span className="mt-1.5 size-2 shrink-0 animate-pulse rounded-full bg-warn" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-warn-ink">
              Configuration drift detected
            </h3>
            {/* The signature can be missing or stale while every value already matches — a
                deployment that was never acknowledged. It still needs a decision, so the banner
                shows without a table rather than leaving the badge with no way to clear it. */}
            <p className="text-sm text-foreground">
              {driftedFields.length > 0
                ? "The following fields have different values in the deployed configuration:"
                : "The deployed configuration has not been acknowledged yet, although its values match the current ones."}
            </p>
          </div>

          {driftedFields.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-warn/25 bg-background">
              <div className="min-w-[520px]">
                <div className="grid grid-cols-[1fr_1.5rem_1.3fr_1.3fr] gap-2 border-b border-warn/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Field</span>
                  <span />
                  <span>Current (DB)</span>
                  <span>Deployed</span>
                </div>
                <div className="divide-y divide-warn/15">
                  {driftedFields.map((field) => {
                    const currentVal = String(
                      current[field as keyof BankTransferConfig] ?? "",
                    );
                    const deployedVal = String(
                      deployed[field as keyof BankTransferConfig] ?? "",
                    );
                    return (
                      <div
                        key={field}
                        className="grid grid-cols-[1fr_1.5rem_1.3fr_1.3fr] items-center gap-2 px-3 py-2 text-sm"
                      >
                        <span className="font-medium">
                          {FIELD_LABELS[field] || field}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 justify-self-center text-muted-foreground/50" />
                        <span
                          className="truncate text-muted-foreground line-through decoration-muted-foreground/50"
                          title={currentVal}
                        >
                          {currentVal || (
                            <span className="italic opacity-60">(empty)</span>
                          )}
                        </span>
                        <span
                          className="truncate font-medium text-ok-ink"
                          title={deployedVal}
                        >
                          {deployedVal || (
                            <span className="italic opacity-60">(empty)</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={onAccept} disabled={isAccepting}>
              {isAccepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Accept deployed values
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="bg-background"
              onClick={onReject}
              disabled={isRejecting}
            >
              {isRejecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Dismissing...
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject (keep current)
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
